/**
 * Générateur — Connaître le sens et les situations d'emploi des opérations.
 * Attendu ID : 692 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 1)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « choisir_operation » — Ex.1 Quelle opération ?
 *     Formats : qcm_unique (4 expressions ; a+b, a−b, a×b, a÷b), texte
 *     (résultat numérique), tableau colonnesLibres (3 situations × 4 opérations).
 *     4 familles : « de plus que » (→ soustraction), « découper en n » (→ ÷),
 *     « prix au kg » (→ ×), « de moins que » (→ addition). Tout en centimes /
 *     millimètres (entiers) ; les résultats tombent juste PAR CONSTRUCTION
 *     (on tire le résultat et un opérande, l'autre s'en déduit).
 *     Misconceptions : pièges additifs du doc (« de plus que » lu comme +,
 *     « de moins que » lu comme −).
 *
 *   Carte « associer_calcul » — Ex.9 Associer calcul et énoncé
 *     Formats : relier (4 paires calcul ↔ énoncé, MÊMES nombres x, y partout),
 *     qcm_unique (un énoncé, 4 calculs), texte (résultat d'un énoncé).
 *     Construction : x, y en dixièmes, contrainte x > 2y (les deux « restes »
 *     sont positifs). Les 4 structures — (2×x)+(2×y), (2×x)+y, x−(2×y),
 *     (x÷2)−y — sont textuellement distinctes par construction.
 *
 *   Carte « question_resoluble » — Ex.19 Quelle question peut-on résoudre ?
 *     Formats : qcm_unique (3 questions, une seule résoluble), texte (réponse).
 *     Unicité STRUCTURELLE : les questions non résolubles portent sur une
 *     grandeur jamais fournie dans l'énoncé (masse, prix, horaire d'arrivée).
 *     3 familles : peinture (V = n×v), trajet (distance hebdo = 10×d),
 *     lecture (P = p×j).
 *
 *   Carte « repartir » — Ex.7 Les étagères (adaptation §0.6 validée : A + B)
 *     A) tableau_vf : une répartition AFFICHÉE, l'élève juge (4 boîtes par
 *        étagère ? charges ≤ Cmax ? répartition valide ?). La dernière ligne
 *        (« la boîte la plus lourde est sur l'étagère 2 ») est FAUSSE par
 *        construction (elle est toujours posée sur l'étagère 1) → jamais
 *        tout-vrai. Cmax est DÉRIVÉ de la répartition tirée (régimes valide /
 *        surcharge / mauvais compte) → vérités connues par construction.
 *     B) classer à symétrie brisée : la boîte la plus lourde est PRÉ-PLACÉE
 *        sur l'étagère 1 (énoncé + nom de catégorie), l'élève classe les 7
 *        autres. Tirage retenu SEULEMENT si la complétion valide est UNIQUE
 *        (énumération des C(7,3) = 35 choix) ; repli vérifié à la main :
 *        masses [190*, 10, 20, 40, 80, 130, 150, 160], cible 390 kg —
 *        complétion unique {10, 40, 150}.
 *
 *   Carte « fois_div » — Créé n°1 (couvre A5 : ×/÷ 10, 100, 1 000)
 *     Formats : texte_trou_saisie, relier (calcul ↔ résultat), qcm_unique.
 *     4 familles en unités entières (centi-kg, g, centimes) → décalage de
 *     virgule EXACT : carton (m × 100), trombones (M ÷ 1 000), ruban (P ÷ 10),
 *     clous (m × 1 000). Misconceptions : décalage dans le mauvais sens,
 *     mauvais nombre de zéros. Le distracteur « 75,00 » (≡ 75) est banni.
 *
 * Adaptations d'énoncés (doc cluster → en ligne) :
 *   - Ex.1 : les cases à cocher deviennent un QCM à 4 expressions + un tableau
 *     colonnesLibres ; les résultats deviennent une saisie séparée (Duolingo).
 *   - Ex.7 : solutions multiples → V/F sur une répartition affichée (A) et
 *     classer à solution unique par symétrie brisée + énumération (B).
 *   - Ex.19 : en famille trajet, seule la distance est calculée (la question
 *     horaire est non résoluble) — conforme à la Remarque du doc.
 */

import {
    randomChoice, randInt,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 692;

const L_CHOIX = 'Choisir la bonne opération';
const L_ASSOC = 'Associer chaque calcul à son énoncé';
const L_QRES  = 'Quelle question peut-on résoudre ?';
const L_REPAR = 'Répartir sous contrainte (étagères)';
const L_10    = 'Multiplier et diviser par 10, 100, 1 000 en contexte';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const PRENOMS_F = ['Alice', 'Inès', 'Maya', 'Sofia', 'Léa', 'Jade'];
const PRENOMS_M = ['Kamel', 'Lucas', 'Nolan', 'Rayan', 'Adam', 'Tom'];

/** Centimes / dixièmes / millièmes (entier) → écriture FR sans zéros finaux. */
const centToFr = (c) => {
    const ent = Math.floor(c / 100);
    const cents = c % 100;
    if (cents === 0) return String(ent);
    return `${ent},${String(cents).padStart(2, '0').replace(/0+$/, '')}`;
};
const dixToFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);
const milToFr = (m) => {
    const ent = Math.floor(m / 1000);
    const mil = m % 1000;
    if (mil === 0) return String(ent);
    return `${ent},${String(mil).padStart(3, '0').replace(/0+$/, '')}`;
};

/** Déduplique des choix par valeur normalisée (virgule ≡ point). */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...candidats, ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Choisir la bonne opération
// ═══════════════════════════════════════════════════════════

/**
 * Chaque famille tire le RÉSULTAT et un opérande, l'autre opérande s'en
 * déduit — le calcul tombe juste par construction. Valeurs en centimes (€),
 * millimètres (cm) ou centimètres (m) selon la famille.
 */
const FAMILLES_OPERATION = [
    {
        id: 'plus_que', colonne: 'Soustraction',
        tirer: () => {
            const res = randInt(300, 1500) + randomChoice([5, 15, 25, 45, 65, 85]); // Y, centimes
            const b = randInt(150, 900) + randomChoice([5, 20, 40, 60, 80]);
            return { a: res + b, b, res };
        },
        enonce: ({ a, b }, qui, autre) =>
            `${qui} a ${centToFr(a)} € dans sa tirelire. ${qui} a ${centToFr(b)} € de plus que ${autre}. ` +
            `Combien ${autre} a-t-il d'argent ?`,
        fmt: centToFr, op: '−', suffixe: '€',
    },
    {
        id: 'decouper', colonne: 'Division',
        tirer: () => {
            const n = randInt(3, 6);
            const res = randInt(20, 90) + randomChoice([1, 3, 7, 9]);  // morceau, mm
            return { a: n * res, b: n, res };
        },
        enonce: ({ a, b }, qui) =>
            `${qui} doit découper ${dixToFr(a)} cm de ruban en ${b} morceaux de même longueur. ` +
            'Quelle est la longueur d\'un morceau ?',
        // b est un COMPTE de morceaux (entier), pas des dixièmes → fmtB dédié
        fmt: dixToFr, fmtB: String, op: '÷', suffixe: 'cm',
    },
    {
        id: 'prix_kg', colonne: 'Multiplication',
        tirer: () => {
            let a, b;
            let essais = 0;
            do {
                a = randInt(12, 49);   // masse en dixièmes de kg (1,2 à 4,9 kg)
                b = randInt(11, 39);   // prix en dixièmes d'€ le kg
                essais++;
            } while ((a * b) % 10 === 0 && essais < 40);
            if ((a * b) % 10 === 0) { a = 28; b = 19; } // repli : 2,8 × 1,9 = 5,32
            return { a, b, res: a * b };  // res en centièmes d'€
        },
        enonce: ({ a, b }, qui) =>
            `${qui} a acheté ${dixToFr(a)} kg de pommes à ${dixToFr(b)} € le kilogramme. ` +
            'Combien cela lui a-t-il coûté ?',
        fmt: dixToFr, fmtRes: centToFr, op: '×', suffixe: '€',
    },
    {
        id: 'moins_que', colonne: 'Addition',
        tirer: () => {
            const a = randInt(350, 700) + randomChoice([1, 3, 6, 7, 9]);  // 2e essai, cm
            const b = randInt(50, 250) + randomChoice([2, 4, 5, 8]);
            return { a, b, res: a + b };
        },
        enonce: ({ a, b }) =>
            `Lors d'un entraînement au saut en longueur, un athlète saute à ${centToFr(a)} m ` +
            `au deuxième essai, soit ${centToFr(b)} m de moins qu'au premier essai. ` +
            'Quelle est la longueur de son premier saut ?',
        fmt: centToFr, op: '+', suffixe: 'm',
    },
];

const tirerOperation = () => {
    const fam = randomChoice(FAMILLES_OPERATION);
    const t = fam.tirer();
    const qui = randomChoice([...PRENOMS_F, ...PRENOMS_M]);
    const autre = randomChoice(PRENOMS_M.filter(p => p !== qui));
    return { fam, t, qui, autre };
};

const genChoixOperationQcm = () => {
    const { fam, t, qui, autre } = tirerOperation();
    const fa = fam.fmt(t.a), fb = (fam.fmtB || fam.fmt)(t.b);
    const bonne = `${fa} ${fam.op} ${fb}`;
    const autresOps = ['+', '−', '×', '÷'].filter(op => op !== fam.op);
    const choix = [bonne, ...autresOps.map(op => `${fa} ${op} ${fb}`)];
    const texte = `<p>${fam.enonce(t, qui, autre)}</p>` +
        '<p>Quel calcul permet de répondre à la question ?</p>';
    return makeQcm(choix, bonne, texte, opts(L_CHOIX));
};

const genChoixOperationSaisie = () => {
    const { fam, t, qui, autre } = tirerOperation();
    const reponse = (fam.fmtRes || fam.fmt)(t.res);
    const texte = `<p>${fam.enonce(t, qui, autre)}</p>` +
        '<p>Écris ta réponse en écriture décimale (ex. : 12,5).</p>';
    return makeSaisie(reponse, texte, { ...opts(L_CHOIX), suffixe: fam.suffixe });
};

const genChoixOperationTableau = () => {
    // 3 situations de familles DISTINCTES → lignes distinctes par construction
    const fams = [...FAMILLES_OPERATION].sort(() => Math.random() - 0.5).slice(0, 3);
    const colonnes = ['Addition', 'Soustraction', 'Multiplication', 'Division'];
    const lignes = [];
    const bonnesReponses = [];
    fams.forEach(fam => {
        const t = fam.tirer();
        const qui = randomChoice([...PRENOMS_F, ...PRENOMS_M]);
        const autre = randomChoice(PRENOMS_M.filter(p => p !== qui));
        lignes.push(fam.enonce(t, qui, autre));
        bonnesReponses.push(colonnes.indexOf(fam.colonne));
    });
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const texte = '<p>Pour chaque situation, coche l\'opération qui permet de répondre à la question.</p>';
    return {
        type: 'qcm',
        texte,
        contenu: { choix: [], bonne_reponse: br },
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'table', config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses } }],
        },
        niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID,
        _meta: { label: L_CHOIX },
    };
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Associer chaque calcul à son énoncé
// ═══════════════════════════════════════════════════════════

/** x, y en dixièmes ; x > 2y garantit les deux « restes » positifs. */
const tirerAssocier = () => {
    const x = randInt(31, 99);                       // 3,1 à 9,9
    const y = randInt(10, Math.floor(x / 2) - 1);    // ⇒ x − 2y ≥ 1 dixième
    return { x, y };
};

/** Les 4 structures de l'exercice source — mêmes nombres dans les 4 énoncés. */
const STRUCTURES_ASSOC = [
    {
        calc: (x, y) => `(2 × ${dixToFr(x)}) + (2 × ${dixToFr(y)})`,
        enonce: (x, y) => `Le périmètre d'un rectangle de longueur ${dixToFr(x)} m et de largeur ${dixToFr(y)} m`,
        res: (x, y) => dixToFr(2 * x + 2 * y), suffixe: 'm',
    },
    {
        calc: (x, y) => `(2 × ${dixToFr(x)}) + ${dixToFr(y)}`,
        enonce: (x, y) => `Le prix de 2 kg de cerises à ${dixToFr(x)} € le kg et d'une pastèque à ${dixToFr(y)} €`,
        res: (x, y) => dixToFr(2 * x + y), suffixe: '€',
    },
    {
        calc: (x, y) => `${dixToFr(x)} − (2 × ${dixToFr(y)})`,
        enonce: (x, y) => `La monnaie restante quand on a ${dixToFr(x)} € et qu'on achète 2 boissons à ${dixToFr(y)} € l'une`,
        res: (x, y) => dixToFr(x - 2 * y), suffixe: '€',
    },
    {
        calc: (x, y) => `(${dixToFr(x)} ÷ 2) − ${dixToFr(y)}`,
        enonce: (x, y) => `Ce qui reste à une enfant qui reçoit la moitié de ${dixToFr(x)} € et dépense ${dixToFr(y)} €`,
        res: (x, y) => centToFr(x * 5 - y * 10), suffixe: '€',   // x/2 € = 5x centimes (x en dixièmes)
    },
];

const genAssocierRelier = () => {
    const { x, y } = tirerAssocier();
    const pairs = STRUCTURES_ASSOC.map(s => ({ source: s.enonce(x, y), target: s.calc(x, y) }));
    const texte = '<p>Les quatre situations utilisent les mêmes nombres. ' +
        'Relie chaque situation au calcul qui permet de la résoudre.</p>';
    return makeRelier(pairs, texte, opts(L_ASSOC));
};

const genAssocierQcm = () => {
    const { x, y } = tirerAssocier();
    const s = randomChoice(STRUCTURES_ASSOC);
    const bonne = s.calc(x, y);
    const autres = STRUCTURES_ASSOC.filter(st => st !== s).map(st => st.calc(x, y));
    const texte = `<p>${s.enonce(x, y)} : quel calcul permet de le trouver ?</p>`;
    return makeQcm([bonne, ...autres], bonne, texte, opts(L_ASSOC));
};

const genAssocierSaisie = () => {
    const { x, y } = tirerAssocier();
    const s = randomChoice(STRUCTURES_ASSOC);
    const texte = `<p>${s.enonce(x, y)} : effectue le calcul. ` +
        'Écris ta réponse en écriture décimale (ex. : 12,5).</p>';
    return makeSaisie(s.res(x, y), texte, { ...opts(L_ASSOC), suffixe: s.suffixe });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Quelle question peut-on résoudre ?
// ═══════════════════════════════════════════════════════════

/**
 * Une seule question est résoluble PAR STRUCTURE : les deux autres portent
 * sur une grandeur jamais fournie (masse, prix, horaire de retour).
 */
const FAMILLES_QRES = [
    {
        tirer: () => {
            const n = randInt(4, 6);
            const v = randInt(15, 55) + randomChoice([0, 2, 4, 6, 8]); // dixièmes de L
            return { n, v, V: n * v };
        },
        enonce: ({ n, V }) =>
            `On répartit équitablement ${dixToFr(V)} L de peinture dans ${n} pots identiques.`,
        resoluble: () => 'Quel volume de peinture contient un pot ?',
        nonResolubles: () => ['Combien pèse un pot ?', 'Combien coûte la peinture ?'],
        reponse: ({ v }) => dixToFr(v), suffixe: 'L',
    },
    {
        tirer: () => ({ d: randInt(52, 349) }),      // dixièmes de km
        enonce: ({ d }) =>
            `Louise habite à ${dixToFr(d)} km de son travail. Elle y va cinq jours par semaine ` +
            'et rentre chez elle chaque soir. Elle travaille de 8 h 30 à 17 h 30.',
        resoluble: () => 'Quelle distance parcourt-elle chaque semaine pour son travail ?',
        nonResolubles: () => ['À quelle heure arrive-t-elle chez elle le soir ?', 'Combien dépense-t-elle en carburant ?'],
        reponse: ({ d }) => dixToFr(d * 10),          // 2 × d × 5 = 10 d
        suffixe: 'km',
    },
    {
        tirer: () => {
            const p = randInt(12, 45);
            const j = randInt(5, 14);
            return { p, j, P: p * j };
        },
        enonce: ({ P, p }) =>
            `Un livre compte ${P} pages. Chaque soir, Naïm lit exactement ${p} pages.`,
        resoluble: () => 'En combien de soirs Naïm finira-t-il son livre ?',
        nonResolubles: () => ['Combien coûte le livre ?', 'Combien de temps Naïm lit-il chaque soir ?'],
        reponse: ({ j }) => String(j), suffixe: 'soirs',
    },
];

const genQuestionResolubleQcm = () => {
    const fam = randomChoice(FAMILLES_QRES);
    const t = fam.tirer();
    const bonne = fam.resoluble();
    const texte = `<p>${fam.enonce(t)}</p>` +
        '<p>Quelle question peut-on résoudre avec les données de l\'énoncé ?</p>';
    return makeQcm([bonne, ...fam.nonResolubles()], bonne, texte, opts(L_QRES));
};

const genQuestionResolubleSaisie = () => {
    const fam = randomChoice(FAMILLES_QRES);
    const t = fam.tirer();
    const texte = `<p>${fam.enonce(t)}</p>` +
        `<p>${fam.resoluble()} Écris ta réponse en écriture décimale si besoin (ex. : 12,5).</p>`;
    return makeSaisie(fam.reponse(t), texte, { ...opts(L_QRES), suffixe: fam.suffixe });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Répartir sous contrainte (étagères)
// ═══════════════════════════════════════════════════════════

/** 8 masses distinctes, multiples de 10 kg, la plus lourde en tête. */
const tirerMasses = () => {
    const pool = [];
    for (let m = 10; m <= 160; m += 10) pool.push(m);
    const sept = [];
    while (sept.length < 7) {
        const m = randomChoice(pool);
        if (!sept.includes(m)) sept.push(m);
    }
    const maxi = Math.max(...sept) + randomChoice([20, 30, 40]);
    return { maxi, autres: sept };
};

const listeHtml = (nom, masses) =>
    `<p><strong>${nom}</strong> : ${masses.map(m => `${m} kg`).join(' — ')}</p>`;

const genRepartirVF = () => {
    const { maxi, autres } = tirerMasses();
    // Répartition AFFICHÉE : la plus lourde toujours sur l'étagère 1.
    const melange = [...autres].sort(() => Math.random() - 0.5);
    const regime = randomChoice(['valide', 'surcharge', 'compte']);
    const nb1 = regime === 'compte' ? 4 : 3;          // É1 = maxi + nb1 boîtes
    const e1 = [maxi, ...melange.slice(0, nb1)];
    const e2 = melange.slice(nb1);
    const s1 = e1.reduce((a, b) => a + b, 0);
    const s2 = e2.reduce((a, b) => a + b, 0);
    // Cmax DÉRIVÉ du régime → vérités connues par construction
    const cmax = regime === 'surcharge' ? Math.max(s1, s2) - 10 : Math.max(s1, s2);
    const quatrePartout = e1.length === 4 && e2.length === 4;
    const ch1Ok = s1 <= cmax;
    const ch2Ok = s2 <= cmax;
    const valide = quatrePartout && ch1Ok && ch2Ok;

    const lignes = [
        'Chaque étagère porte exactement quatre boîtes',
        `La charge de l'étagère 1 ne dépasse pas ${cmax} kg`,
        `La charge de l'étagère 2 ne dépasse pas ${cmax} kg`,
        'Cette répartition respecte toutes les règles',
        `La boîte la plus lourde (${maxi} kg) est posée sur l'étagère 2`,
    ];
    const bonnes = [
        quatrePartout ? 0 : 1,
        ch1Ok ? 0 : 1,
        ch2Ok ? 0 : 1,
        valide ? 0 : 1,
        1,                          // FAUSSE par construction (elle est sur l'É1)
    ];
    const texte =
        '<p>On doit ranger huit boîtes sur deux étagères : chaque étagère doit porter ' +
        `<strong>exactement quatre boîtes</strong> sans dépasser <strong>${cmax} kg</strong>. ` +
        'Voici la répartition proposée :</p>' +
        listeHtml('Étagère 1', e1) + listeHtml('Étagère 2', e2) +
        '<p>Indique si chaque affirmation est vraie ou fausse.</p>';
    return makeVraiFaux(lignes, bonnes, texte, opts(L_REPAR));
};

/**
 * Classer à symétrie brisée : la plus lourde est PRÉ-PLACÉE sur l'étagère 1.
 * On ne garde un tirage que si UNE SEULE complétion (3 boîtes parmi 7) atteint
 * exactement la cible — énumération des 35 triplets. Repli vérifié à la main.
 */
const REPLI_CLASSER = {
    maxi: 190, autres: [10, 20, 40, 80, 130, 150, 160],
    e1: [10, 40, 150], cible: 390,
};

const completionsValides = (maxi, autres, cible) => {
    const res = [];
    for (let i = 0; i < 7; i++) {
        for (let j = i + 1; j < 7; j++) {
            for (let k = j + 1; k < 7; k++) {
                if (maxi + autres[i] + autres[j] + autres[k] === cible) {
                    res.push([autres[i], autres[j], autres[k]]);
                }
            }
        }
    }
    return res;
};

const tirerClasser = () => {
    for (let essai = 0; essai < 60; essai++) {
        const { maxi, autres } = tirerMasses();
        const total = maxi + autres.reduce((a, b) => a + b, 0);
        if (total % 2 !== 0) continue;
        const cible = total / 2;
        const sols = completionsValides(maxi, autres, cible);
        if (sols.length === 1) return { maxi, autres, e1: sols[0], cible };
    }
    return REPLI_CLASSER;
};

const genRepartirClasser = () => {
    const { maxi, autres, e1, cible } = tirerClasser();
    const e2 = autres.filter(m => !e1.includes(m));
    const categories = [
        { name: `Étagère 1 (avec la boîte de ${maxi} kg)`, items: e1.map(m => `${m} kg`) },
        { name: 'Étagère 2', items: e2.map(m => `${m} kg`) },
    ];
    const texte =
        '<p>Huit boîtes doivent être rangées sur deux étagères : ' +
        `<strong>exactement quatre boîtes</strong> par étagère et <strong>${cible} kg maximum</strong> par étagère. ` +
        `La boîte la plus lourde (<strong>${maxi} kg</strong>) est déjà posée sur l'étagère 1.</p>` +
        `<p>Boîtes restantes : ${autres.map(m => `${m} kg`).join(' — ')}. Place-les.</p>`;
    return makeClasser(categories, texte, opts(L_REPAR));
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Multiplier et diviser par 10, 100, 1 000
// ═══════════════════════════════════════════════════════════

/**
 * Familles en unités entières → décalage de virgule exact. `val` est la donnée
 * affichée, `res` le résultat ; les deux formatés canoniquement (sans zéro final).
 */
const FAMILLES_10 = [
    {
        tirer: () => {
            const m = randInt(25, 95);               // centi-kg, jamais multiple de 10
            return m % 10 === 0 ? { m: m + 5 } : { m };
        },
        calc: ({ m }) => `${centToFr(m)} × 100`,
        enonce: ({ m }) => `Un carton contient 100 boîtes de ${centToFr(m)} kg chacune. Quelle est la masse du carton ?`,
        res: ({ m }) => String(m),                    // 0,75 × 100 = 75
        pieges: ({ m }) => [centToFr(m * 10), milToFr(m), String(m * 10)],
        suffixe: 'kg', conclusion: 'le carton pèse',
    },
    {
        tirer: () => ({ M: randInt(3, 19) * 50 }),    // g pour 1 000 trombones
        calc: ({ M }) => `${M} ÷ 1 000`,
        enonce: ({ M }) => `1 000 trombones pèsent ${M} g. Quelle est la masse d'un trombone ?`,
        res: ({ M }) => milToFr(M),                   // 350 ÷ 1000 = 0,35
        pieges: ({ M }) => [centToFr(M), dixToFr(M), String(M * 1000)],
        suffixe: 'g', conclusion: 'un trombone pèse',
    },
    {
        tirer: () => ({ P: randInt(25, 95) * 10 }),   // centimes pour 10 m
        calc: ({ P }) => `${centToFr(P)} ÷ 10`,
        enonce: ({ P }) => `Un rouleau de 10 m de ruban coûte ${centToFr(P)} €. Quel est le prix d'un mètre de ruban ?`,
        res: ({ P }) => centToFr(P / 10),             // 4,5 ÷ 10 = 0,45
        pieges: ({ P }) => [centToFr(P * 10), centToFr(P), milToFr(P / 10)],
        suffixe: '€', conclusion: 'un mètre de ruban coûte',
    },
    {
        tirer: () => {
            const g = randInt(2, 9) * 10 + randomChoice([2, 4, 6, 8]);  // déci-g
            return { g };
        },
        calc: ({ g }) => `${dixToFr(g)} × 1 000`,
        enonce: ({ g }) => `Un clou pèse ${dixToFr(g)} g. Combien pèsent 1 000 clous ?`,
        res: ({ g }) => String(g * 100),              // 6,4 × 1000 = 6400
        pieges: ({ g }) => [String(g * 10), dixToFr(g * 10), String(g * 1000)],
        suffixe: 'g', conclusion: '1 000 clous pèsent',
    },
];

const genFoisDivTrouSaisie = () => {
    const fam = randomChoice(FAMILLES_10);
    const t = fam.tirer();
    const res = fam.res(t);
    const phrase = `${fam.calc(t)} = {{${res}}} : ${fam.conclusion} {{${res}}} ${fam.suffixe}.`;
    const texte = `<p>${fam.enonce(t)}</p>` +
        '<p>Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_10), mode: 'saisie' });
};

const genFoisDivQcm = () => {
    const fam = randomChoice(FAMILLES_10);
    const t = fam.tirer();
    const bonne = fam.res(t);
    const distracteurs = dedupeChoix(bonne, fam.pieges(t), []);
    const texte = `<p>${fam.enonce(t)}</p>`;
    return makeQcm(
        [`${bonne} ${fam.suffixe}`, ...distracteurs.map(d => `${d} ${fam.suffixe}`)],
        `${bonne} ${fam.suffixe}`, texte, opts(L_10),
    );
};

/**
 * Relier : UN SEUL nombre de base décliné en ×10/×100/×1 000/÷10/÷100/÷1 000.
 * Tous les résultats partagent les MÊMES chiffres — seule la virgule bouge :
 * l'élève doit raisonner le sens et l'amplitude du décalage (avec des nombres
 * différents, l'association se lisait par simple reconnaissance des chiffres —
 * défaut relevé par le responsable pédagogique).
 */
const OPS_10 = [
    { libelle: '× 10',    resDeTenths: (t) => String(t) },                       // 6,4 → 64
    { libelle: '× 100',   resDeTenths: (t) => String(t * 10) },                  // 6,4 → 640
    { libelle: '× 1 000', resDeTenths: (t) => String(t * 100) },                 // 6,4 → 6400
    { libelle: '÷ 10',    resDeTenths: (t) => centToFr(t) },                     // 6,4 → 0,64
    { libelle: '÷ 100',   resDeTenths: (t) => milToFr(t) },                      // 6,4 → 0,064
    { libelle: '÷ 1 000', resDeTenths: (t) => `0,${String(t).padStart(4, '0')}` },  // 6,4 → 0,0064
];

const genFoisDivRelier = () => {
    // Base à 1 décimale, jamais multiple de 10 (pas de zéro final ambigu)
    let t = randInt(11, 99);
    if (t % 10 === 0) t += randomChoice([1, 3, 7]);
    const base = dixToFr(t);
    // 2 multiplications + 2 divisions parmi les 6 opérations → décalages tous
    // différents → résultats distincts par construction (mêmes chiffres,
    // virgules à des positions différentes).
    const mults = [...OPS_10.slice(0, 3)].sort(() => Math.random() - 0.5).slice(0, 2);
    const divs = [...OPS_10.slice(3)].sort(() => Math.random() - 0.5).slice(0, 2);
    const pairs = [...mults, ...divs].map(op => ({
        source: `${base} ${op.libelle}`,
        target: op.resDeTenths(t),
    }));
    const texte = '<p>Tous les calculs partent du même nombre. Relie chaque calcul à son résultat.</p>';
    return makeRelier(pairs, texte, opts(L_10));
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    choisir_operation_qcm_unique: genChoixOperationQcm,
    choisir_operation_texte:      genChoixOperationSaisie,
    choisir_operation_tableau:    genChoixOperationTableau,
    associer_calcul_relier:       genAssocierRelier,
    associer_calcul_qcm_unique:   genAssocierQcm,
    associer_calcul_texte:        genAssocierSaisie,
    question_resoluble_qcm_unique: genQuestionResolubleQcm,
    question_resoluble_texte:      genQuestionResolubleSaisie,
    repartir_tableau_vf:          genRepartirVF,
    repartir_classer:             genRepartirClasser,
    fois_div_texte_trou_saisie:   genFoisDivTrouSaisie,
    fois_div_qcm_unique:          genFoisDivQcm,
    fois_div_relier:              genFoisDivRelier,
};

export const CATEGORIES = [
    {
        id: 'choisir_operation', label: L_CHOIX,
        formatsList: [
            { key: 'choisir_operation_qcm_unique', label: 'QCM' },
            { key: 'choisir_operation_texte',      label: 'Saisie' },
            { key: 'choisir_operation_tableau',    label: 'Tableau' },
        ],
    },
    {
        id: 'associer_calcul', label: L_ASSOC,
        formatsList: [
            { key: 'associer_calcul_relier',     label: 'Relier' },
            { key: 'associer_calcul_qcm_unique', label: 'QCM' },
            { key: 'associer_calcul_texte',      label: 'Saisie' },
        ],
    },
    {
        id: 'question_resoluble', label: L_QRES,
        formatsList: [
            { key: 'question_resoluble_qcm_unique', label: 'QCM' },
            { key: 'question_resoluble_texte',      label: 'Saisie' },
        ],
    },
    {
        id: 'repartir', label: L_REPAR,
        formatsList: [
            { key: 'repartir_tableau_vf', label: 'Vrai/Faux' },
            { key: 'repartir_classer',    label: 'Classer' },
        ],
    },
    {
        id: 'fois_div', label: L_10,
        formatsList: [
            { key: 'fois_div_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'fois_div_qcm_unique',        label: 'QCM' },
            { key: 'fois_div_relier',            label: 'Relier' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    centToFr, dixToFr, milToFr, dedupeChoix,
    FAMILLES_OPERATION, STRUCTURES_ASSOC, FAMILLES_QRES, FAMILLES_10,
    tirerMasses, tirerClasser, completionsValides, REPLI_CLASSER,
};
